import React, { useState, useEffect } from 'react';
import { Form, Input, Select, DatePicker, Button, Space, Row, Col, message } from 'antd';
import firebase from 'firebase/app';
import moment from 'moment';

const { TextArea } = Input;
const { Option } = Select;

const AdminAnnouncementForm = ({ announcement, onSubmit, onCancel, loading }) => {
    const [form] = Form.useForm();
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    useEffect(() => {
        if (announcement) {
            // Populate form with existing announcement data
            const formData = {
                headline: announcement.headline,
                message: announcement.message,
                type: announcement.type,
                status: announcement.status,
                maxCount: announcement.maxCount,
            };

            if (announcement.startAt) {
                setStartDate(moment(announcement.startAt));
                formData.startAt = moment(announcement.startAt);
            }

            if (announcement.endAt) {
                setEndDate(moment(announcement.endAt));
                formData.endAt = moment(announcement.endAt);
            }

            form.setFieldsValue(formData);
        } else {
            // Set default values for new announcement
            form.setFieldsValue({
                status: 'active',
                type: 'info',
                maxCount: 1000,
            });
            setStartDate(moment());
        }
    }, [announcement, form]);

    const handleSubmit = async (values) => {
        try {
            const formData = {
                headline: values.headline,
                message: values.message,
                type: values.type,
                status: values.status,
                maxCount: values.maxCount || 1000,
                startAt: values.startAt ? firebase.firestore.Timestamp.fromDate(values.startAt.toDate()) : null,
                endAt: values.endAt ? firebase.firestore.Timestamp.fromDate(values.endAt.toDate()) : null,
            };

            await onSubmit(formData);
        } catch (error) {
            console.error('Form submission error:', error);
            message.error('Failed to save announcement');
        }
    };

    const handleStartDateChange = (date) => {
        setStartDate(date);
        form.setFieldsValue({ startAt: date });
    };

    const handleEndDateChange = (date) => {
        setEndDate(date);
        form.setFieldsValue({ endAt: date });
    };

    const disabledEndDate = (current) => {
        if (!startDate) return false;
        return current && current < startDate.startOf('day');
    };

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
                status: 'active',
                type: 'info',
                maxCount: 1000,
            }}
        >
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item
                        name="headline"
                        label="Headline"
                        rules={[
                            { required: true, message: 'Please enter a headline' },
                            { max: 100, message: 'Headline must be 100 characters or less' }
                        ]}
                    >
                        <Input placeholder="Enter announcement headline" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        name="type"
                        label="Type"
                        rules={[{ required: true, message: 'Please select a type' }]}
                    >
                        <Select placeholder="Select announcement type">
                            <Option value="info">Info</Option>
                            <Option value="warning">Warning</Option>
                            <Option value="error">Error</Option>
                            <Option value="marketing">Marketing</Option>
                        </Select>
                    </Form.Item>
                </Col>
            </Row>

            <Form.Item
                name="message"
                label="Message"
                rules={[
                    { required: true, message: 'Please enter a message' },
                    { max: 500, message: 'Message must be 500 characters or less' }
                ]}
            >
                <TextArea 
                    rows={4} 
                    placeholder="Enter announcement message"
                    showCount
                    maxLength={500}
                />
            </Form.Item>

            <Row gutter={16}>
                <Col span={8}>
                    <Form.Item
                        name="status"
                        label="Status"
                        rules={[{ required: true, message: 'Please select a status' }]}
                    >
                        <Select placeholder="Select status">
                            <Option value="active">Active</Option>
                            <Option value="paused">Paused</Option>
                            <Option value="completed">Completed</Option>
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item
                        name="maxCount"
                        label="Max Views"
                        rules={[
                            { type: 'number', min: 1, message: 'Must be at least 1' }
                        ]}
                    >
                        <Input 
                            type="number" 
                            placeholder="1000"
                            min={1}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item
                        name="startAt"
                        label="Start Date & Time"
                        rules={[{ required: true, message: 'Please select start date' }]}
                    >
                        <DatePicker
                            showTime
                            format="YYYY-MM-DD HH:mm"
                            placeholder="Select start date and time"
                            value={startDate}
                            onChange={handleStartDateChange}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        name="endAt"
                        label="End Date & Time"
                    >
                        <DatePicker
                            showTime
                            format="YYYY-MM-DD HH:mm"
                            placeholder="Select end date and time"
                            value={endDate}
                            onChange={handleEndDateChange}
                            disabledDate={disabledEndDate}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space>
                    <Button onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button type="primary" htmlType="submit" loading={loading}>
                        {announcement ? 'Update Announcement' : 'Create Announcement'}
                    </Button>
                </Space>
            </Form.Item>
        </Form>
    );
};

export default AdminAnnouncementForm;
